function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Card, CardBody, CardHeader, Button } from 'reactstrap';
import { buildCabinetReplaceViewQuery } from '../../utils/cabinetDeepLinkFilters';
import { compareSummary, getCompareEntry, isNumericRangeFilter, withoutCompareFilters } from '../../utils/cabinetCompareFilters';
import { removeUnavailableFilters } from '../../utils/cabinetFilterUtils';
import CabinetCompareFilter from './cabinetCompareFilter';
import CabinetFilter from './cabinetFilter';
import CabinetFilterCustomizer from './cabinetFilterCustomizer';
import ControlFilterSwitch from '../common/controls/controlFilterSwitch';
import entityComposition from '../common/classes/entityComposition';
import Icon from '../common/containers/icon';
import FullTextInstructions from '../common/search/fullTextInstructions';
import SearchInput from '../common/search/searchInput';
import SearchOptions from '../common/search/searchOptions';
import ShowDeletedFilter from '../common/filters/showDeletedFilter';
import AiChatModal from './aiChatModal';
import SavedFormFilters from './savedFormFilters';

// must be declared here so the ref callback can refer to it
let controlFilterSwitch = null;
const CabinetFilterPanel = ({
  cabinetIdentifier,
  cabinetActions: {
    serverFilter,
    serverReplaceView
  },
  currentCabinet: {
    indexQuery,
    aiError,
    aiRequest,
    aiSuccess
  },
  currentCabinet,
  filterDefinitions,
  candidateFilters: _candidateFilters = [],
  addedFilterIndexes: _addedFilterIndexes = [],
  onSaveFilters: _onSaveFilters = undefined,
  filterPreferencesReady: _filterPreferencesReady = true,
  formTypeIdentifiers: _formTypeIdentifiers = [],
  cabinetFilterOptions,
  cabinetFilterOptionsActions,
  searchPlaceholder: _searchPlaceholder = 'Search',
  aiSearchConfig: _aiSearchConfig = undefined,
  username: _username = ''
}) => {
  var _aiSearchConfig$promp, _aiSearchConfig$waiti, _aiSearchConfig$title, _aiSearchConfig$butto, _aiSearchConfig$empty, _indexQuery$filters$m, _indexQuery$filters, _indexQuery$filters2, _indexQuery$filters3, _indexQuery$filters4, _indexQuery$filters5, _indexQuery$filters6;
  // Admin user check - easy to modify later to enable for all users
  // To enable for all users, simply change this to: const isAdminUser = true;
  const isAdminUser = _username === 'truqc';
  // Merge aiSearchConfig with defaults to handle partial configurations
  const mergedAiSearchConfig = _aiSearchConfig ? {
    promptPlaceholder: (_aiSearchConfig$promp = _aiSearchConfig.promptPlaceholder) !== null && _aiSearchConfig$promp !== void 0 ? _aiSearchConfig$promp : 'Ask AI anything...',
    waitingMessage: (_aiSearchConfig$waiti = _aiSearchConfig.waitingMessage) !== null && _aiSearchConfig$waiti !== void 0 ? _aiSearchConfig$waiti : 'Thinking...',
    title: (_aiSearchConfig$title = _aiSearchConfig.title) !== null && _aiSearchConfig$title !== void 0 ? _aiSearchConfig$title : 'AI Search',
    buttonTitle: (_aiSearchConfig$butto = _aiSearchConfig.buttonTitle) !== null && _aiSearchConfig$butto !== void 0 ? _aiSearchConfig$butto : 'AI Search',
    emptyStateMessage: (_aiSearchConfig$empty = _aiSearchConfig.emptyStateMessage) !== null && _aiSearchConfig$empty !== void 0 ? _aiSearchConfig$empty : 'Start a conversation with AI'
  } : null;

  // Modal state
  const [isAiChatModalOpen, setIsAiChatModalOpen] = useState(false);
  const [isFilterCustomizerOpen, setIsFilterCustomizerOpen] = useState(false);
  const [hoverTimer, setHoverTimer] = useState(null);
  const aiSearchButtonRef = useRef(null);
  const prevAiSuccessRef = useRef(false);
  /** Serialize filter-option prefetches across effect re-runs (stale `filters` deps). */
  const filterPrefetchRunIdRef = useRef(0);
  const filterPrefetchTailRef = useRef(Promise.resolve());

  // Convert AI error to chat message format
  const errorMessages = aiError ? [{
    id: `error-${Date.now()}`,
    content: aiError,
    type: 'system',
    timestamp: new Date().toISOString()
  }] : [];
  const filterText = textValue => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      text: textValue
    });
    // don't try to filter if indexQuery isn't initialized
    if (indexQuery) serverFilter(newFilters, indexQuery);
  };
  const updateSearchOption = (key, value) => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      [key]: value
    });
    if (indexQuery) serverFilter(newFilters, indexQuery);
  };

  // Modal handlers
  const openAiChatModal = () => {
    setIsAiChatModalOpen(true);
  };
  const closeAiChatModal = () => {
    setIsAiChatModalOpen(false);
  };
  const filterAISearch = async aiSearchValue => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      aiSearch: aiSearchValue
    });
    // don't try to filter if indexQuery isn't initialized
    if (indexQuery) {
      await serverFilter(newFilters, indexQuery);
    }
  };
  const handleAiSearchButtonClick = () => {
    openAiChatModal();
  };
  const handleAiSearchButtonMouseEnter = () => {
    const timer = setTimeout(() => {
      openAiChatModal();
    }, 800); // 0.8 seconds
    setHoverTimer(timer);
  };
  const handleAiSearchButtonMouseLeave = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
  };

  // Clean up timer on unmount
  useEffect(() => () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
    }
  }, [hoverTimer]);

  // Close modal only when AI success transitions from false -> true
  useEffect(() => {
    if (aiSuccess && !prevAiSuccessRef.current) {
      closeAiChatModal();
    }
    prevAiSuccessRef.current = aiSuccess;
  }, [aiSuccess]);

  /**
   *  Passed to CabinetFilter and on to BreadcrumbsBack
   */
  const handleBack = () => {
    if (controlFilterSwitch !== null) {
      controlFilterSwitch.controlSwitch.switchControl();
    }
  };

  // Reset the filter depth view whenever you switch to a different cabinet
  useEffect(() => {
    // passing null/nothing into the switchContorl method takes you to the top of the heirarchy
    controlFilterSwitch.controlSwitch.switchControl();
  }, [cabinetFilterOptionsActions, cabinetIdentifier, filterDefinitions]);

  /**
   *  Clear selected filters
   */
  const handleClear = () => {
    const newFilters = _objectSpread(_objectSpread({}, withoutCompareFilters(indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters)), {}, {
      text: '',
      textOperator: 'and',
      textMatchMode: 'contains',
      aiSearch: '',
      metaDeleted: 0 // Reset to default instead of deleting
    });
    filterDefinitions.forEach(({
      index
    }) => {
      if (newFilters[index]) {
        delete newFilters[index];
      }
    });
    return serverFilter(newFilters, indexQuery);
  };
  const filterTextClear = () => filterText();

  /**
   *  Save the filters the user chose to show, then drop the selections of any filter
   *  they removed so the list reloads without those criteria.
   */
  const handleSaveFilters = async newIndexes => {
    const saved = await _onSaveFilters(newIndexes);
    if (!saved) {
      return;
    }
    setIsFilterCustomizerOpen(false);
    const currentFilters = (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {};
    const removedWithSelections = _addedFilterIndexes.filter(index => !newIndexes.includes(index) && currentFilters[index]);
    if (!indexQuery || !removedWithSelections.length) {
      return;
    }
    const newFilters = _objectSpread({}, currentFilters);
    removedWithSelections.forEach(index => {
      delete newFilters[index];
    });
    await serverFilter(newFilters, indexQuery);
  };

  /**
   *  Handle the metaDeleted filter change
   */
  const handleMetaDeletedChange = value => {
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      metaDeleted: value
    });
    if (indexQuery) serverFilter(newFilters, indexQuery);
  };
  const filterAISearchClear = () => {
    // Clear AI search and any errors
    const newFilters = _objectSpread(_objectSpread({}, (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}), {}, {
      aiSearch: ''
    });
    if (indexQuery) {
      serverFilter(newFilters, indexQuery);
    }
  };

  // Rail summary labels: not memoized so we always read latest options list after fetch.
  const getFilterTitles = idx => {
    var _bucket$idx;
    const bucket = cabinetFilterOptions[cabinetIdentifier] || cabinetFilterOptions[cabinetFilterOptions === null || cabinetFilterOptions === void 0 ? void 0 : cabinetFilterOptions.currentCabinet] || {};
    const currentCabinetFilterOptions = ((_bucket$idx = bucket[idx]) === null || _bucket$idx === void 0 ? void 0 : _bucket$idx.list) || [];
    const {
      filters = {}
    } = indexQuery || {};
    const currentSelections = filters[idx] || [];
    const titles = [];
    const comparison = compareSummary(getCompareEntry(filters, idx));
    if (comparison) {
      titles.push(comparison);
    }
    currentSelections.forEach(selection => {
      if (Array.isArray(selection)) {
        if (selection.length === 1) {
          const id = selection[0];
          if (id === null || typeof id === 'string' || typeof id === 'number') {
            if (id === null) {
              titles.push('No selection');
            } else {
              const opt = currentCabinetFilterOptions.find(option => option.id === id || option.value === id) || {};
              const title = opt.name || id;
              titles.push(title);
            }
          }
        } else {
          selection.forEach(name => {
            if (typeof name === 'string') {
              titles.push(name);
            }
          });
        }
      }
    });
    return titles;
  };

  // Prefetch options for filters that already have selections (URL, saved view) so lists
  // load without drill-in. Separate from UUID→display-name normalization.
  useEffect(() => {
    const filters = indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters;
    if (!cabinetIdentifier || !filters) {
      return undefined;
    }
    filterPrefetchRunIdRef.current += 1;
    const runId = filterPrefetchRunIdRef.current;
    const maybePrefetchOne = async filterDef => {
      const idx = filterDef.index;
      const raw = filters[idx];
      if (!Array.isArray(raw) || raw.length === 0) {
        return;
      }
      const selectionNeedsLookup = raw.some(entry => Array.isArray(entry) && entry.length === 1 && entry[0] !== null && entry[0] !== '' && (typeof entry[0] === 'string' || typeof entry[0] === 'number'));
      if (!selectionNeedsLookup) {
        return;
      }
      const bucket = cabinetFilterOptions[cabinetIdentifier] || cabinetFilterOptions[cabinetFilterOptions === null || cabinetFilterOptions === void 0 ? void 0 : cabinetFilterOptions.currentCabinet] || {};
      const slot = bucket[idx] || {};
      const {
        error,
        indexQuery: filterOptionsIndexQuery = {},
        list = [],
        loading: slotLoading = false
      } = slot;
      if (slotLoading || error) {
        return;
      }
      if (list.length > 0) {
        return;
      }
      if (filterOptionsIndexQuery.initialized && filterOptionsIndexQuery.cabinet === cabinetIdentifier && filterOptionsIndexQuery.filterIndex === idx && !filterOptionsIndexQuery.error && !error) {
        return;
      }
      await cabinetFilterOptionsActions.serverIndex(cabinetIdentifier, idx, filterOptionsIndexQuery);
    };
    filterPrefetchTailRef.current = filterPrefetchTailRef.current.then(async () => {
      /* eslint-disable no-await-in-loop -- cabinetFilterOptions shares entityActionFactory's
         single-slot pendingQueryQueue; parallel serverIndex drops queued work. */
      for (let i = 0; i < filterDefinitions.length; i += 1) {
        if (runId !== filterPrefetchRunIdRef.current) {
          return;
        }
        await maybePrefetchOne(filterDefinitions[i]);
      }
    }).catch(() => {});
    return undefined;
  }, [cabinetFilterOptions, cabinetFilterOptionsActions, cabinetIdentifier, filterDefinitions, indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters]);
  const replaceFilters = async savedView => {
    await serverReplaceView(buildCabinetReplaceViewQuery({
      indexQuery,
      initialIndexQuery: {},
      savedView,
      formTypeIdentifiers: _formTypeIdentifiers,
      cabinetIdentifier,
      filterDefinitions
    }));
    handleBack();
  };

  // Create Show Deleted filter control (for admin users)
  const showDeletedFilterControl = isAdminUser ? {
    label: 'Show Deleted',
    entity: 'ShowDeleted',
    filterName: 'metaDeleted',
    control: /*#__PURE__*/React.createElement(ShowDeletedFilter, {
      value: (_indexQuery$filters$m = indexQuery === null || indexQuery === void 0 || (_indexQuery$filters = indexQuery.filters) === null || _indexQuery$filters === void 0 ? void 0 : _indexQuery$filters.metaDeleted) !== null && _indexQuery$filters$m !== void 0 ? _indexQuery$filters$m : 0,
      onChange: handleMetaDeletedChange,
      onBack: handleBack
    }),
    titleValues: [(indexQuery === null || indexQuery === void 0 || (_indexQuery$filters2 = indexQuery.filters) === null || _indexQuery$filters2 === void 0 ? void 0 : _indexQuery$filters2.metaDeleted) === 1 ? 'Yes' : 'No'],
    filters: (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}
  } : null;

  // a filter the cabinet does not define on the server can never load options, so it is left
  // out of the rail rather than failing every time it is opened
  const availableFilterDefinitions = removeUnavailableFilters(filterDefinitions, cabinetFilterOptions[cabinetIdentifier], indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters);

  // create a filter detail component for each filterDefinition. numericRange filters compare
  // the indexed value instead of picking from a list, so they get the comparison view.
  const cabinetFilterControls = availableFilterDefinitions.map(filterDef => ({
    filterName: filterDef.index,
    label: filterDef.title,
    entity: `${cabinetIdentifier}_${filterDef.index}`,
    control: isNumericRangeFilter(filterDef) ? /*#__PURE__*/React.createElement(CabinetCompareFilter, {
      filterDefinition: filterDef,
      indexQuery: indexQuery || {
        filters: {}
      },
      onBack: handleBack,
      serverFilter: serverFilter
    }) : /*#__PURE__*/React.createElement(CabinetFilter, {
      cabinetIdentifier: cabinetIdentifier,
      filterDefinition: filterDef,
      indexQuery: indexQuery || {
        filters: {}
      },
      onBack: handleBack,
      serverFilter: serverFilter
    }),
    titleValues: () => getFilterTitles(filterDef.index),
    filters: (indexQuery === null || indexQuery === void 0 ? void 0 : indexQuery.filters) || {}
  }));

  // Combine filters - Show Deleted first (if admin), then cabinet filters
  const filterControls = showDeletedFilterControl ? [showDeletedFilterControl, ...cabinetFilterControls] : cabinetFilterControls;

  // Customizing is only offered when the cabinet has indexes it does not already filter on
  const canCustomizeFilters = Boolean(_onSaveFilters) && _candidateFilters.length > 0;
  // what is left once the user's own filters are taken out is the cabinet's own
  const staticFilters = filterDefinitions.filter(({
    index
  }) => !_addedFilterIndexes.includes(index));
  const textOperator = (indexQuery === null || indexQuery === void 0 || (_indexQuery$filters3 = indexQuery.filters) === null || _indexQuery$filters3 === void 0 ? void 0 : _indexQuery$filters3.textOperator) === 'or' ? 'or' : 'and';
  const textMatchMode = (indexQuery === null || indexQuery === void 0 || (_indexQuery$filters4 = indexQuery.filters) === null || _indexQuery$filters4 === void 0 ? void 0 : _indexQuery$filters4.textMatchMode) === 'exact' ? 'exact' : 'contains';
  const searchLabel = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FullTextInstructions, null), /*#__PURE__*/React.createElement(SearchOptions, {
    operator: textOperator,
    matchMode: textMatchMode,
    onChange: updateSearchOption
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, {
    "data-testid": "cabinet-filter-panel"
  }, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading"
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: 8
  }, /*#__PURE__*/React.createElement("h3", {
    className: "t-ifp-title"
  }, "Filter")), /*#__PURE__*/React.createElement(Col, {
    xs: 4
  }, /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    onClick: handleClear,
    "data-testid": "t-cf-clear",
    className: "float-md-right t-cf-clear"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "times-circle",
    weight: "far"
  }), " Clear")))), /*#__PURE__*/React.createElement(CardBody, null, mergedAiSearchConfig && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    ref: aiSearchButtonRef,
    onClick: handleAiSearchButtonClick,
    onMouseEnter: handleAiSearchButtonMouseEnter,
    onMouseLeave: handleAiSearchButtonMouseLeave,
    "data-testid": "t-cf-ai-search-button",
    block: true,
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "magic-wand-sparkles"
  }), " ", mergedAiSearchConfig.buttonTitle), /*#__PURE__*/React.createElement(AiChatModal, {
    isOpen: isAiChatModalOpen,
    onToggle: closeAiChatModal,
    onSendMessage: filterAISearch,
    onClear: filterAISearchClear,
    messages: errorMessages,
    isLoading: aiRequest,
    label: /*#__PURE__*/React.createElement("span", null, mergedAiSearchConfig.title),
    placeholder: mergedAiSearchConfig.promptPlaceholder,
    text: (indexQuery === null || indexQuery === void 0 || (_indexQuery$filters5 = indexQuery.filters) === null || _indexQuery$filters5 === void 0 ? void 0 : _indexQuery$filters5.aiSearch) || '',
    waitingMessage: mergedAiSearchConfig.waitingMessage,
    emptyStateMessage: mergedAiSearchConfig.emptyStateMessage
  })), /*#__PURE__*/React.createElement(SearchInput, {
    className: "t-cf-search",
    type: "text",
    label: searchLabel,
    text: (indexQuery === null || indexQuery === void 0 || (_indexQuery$filters6 = indexQuery.filters) === null || _indexQuery$filters6 === void 0 ? void 0 : _indexQuery$filters6.text) || '',
    onChange: filterText,
    onClear: filterTextClear,
    placeholder: _searchPlaceholder
  }), /*#__PURE__*/React.createElement(ControlFilterSwitch, {
    className: "t-cfp-filters",
    filterControls: filterControls,
    ref: input => {
      controlFilterSwitch = input;
    }
  }), canCustomizeFilters && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    block: true,
    className: "mt-2 t-cf-customize",
    "data-testid": "t-cf-customize",
    disabled: !_filterPreferencesReady,
    title: _filterPreferencesReady ? 'Customize' : 'Loading your preferences',
    onClick: () => setIsFilterCustomizerOpen(true)
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "sliders"
  }), " Customize"), /*#__PURE__*/React.createElement(CabinetFilterCustomizer, {
    isOpen: isFilterCustomizerOpen,
    staticFilters: staticFilters,
    candidateFilters: _candidateFilters,
    addedFilterIndexes: _addedFilterIndexes,
    onSave: handleSaveFilters,
    onClose: () => setIsFilterCustomizerOpen(false)
  })))), /*#__PURE__*/React.createElement(Card, {
    className: "filters"
  }, /*#__PURE__*/React.createElement(CardHeader, {
    className: "card-heading"
  }, /*#__PURE__*/React.createElement("h3", null, "Saved Filters")), /*#__PURE__*/React.createElement(CardBody, null, /*#__PURE__*/React.createElement(SavedFormFilters, {
    cabinetIdentifier: cabinetIdentifier,
    cabinet: currentCabinet,
    replaceFilters: replaceFilters,
    filterDefinitions: filterDefinitions
  }))));
};
CabinetFilterPanel.propTypes = {
  cabinetIdentifier: PropTypes.string.isRequired,
  formTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
  filterDefinitions: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.string,
    title: PropTypes.string,
    // 'numericRange' swaps the value list for a min/max comparison control
    type: PropTypes.string
  })).isRequired,
  candidateFilters: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })),
  addedFilterIndexes: PropTypes.arrayOf(PropTypes.string),
  onSaveFilters: PropTypes.func,
  filterPreferencesReady: PropTypes.bool,
  currentCabinet: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({
        text: PropTypes.string,
        textOperator: PropTypes.oneOf(['and', 'or']),
        textMatchMode: PropTypes.oneOf(['contains', 'exact']),
        aiSearch: PropTypes.string,
        formTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
        metaDeleted: PropTypes.number
      })
    }),
    initialIndexQuery: PropTypes.shape({}),
    aiError: PropTypes.string,
    aiRequest: PropTypes.bool,
    aiSuccess: PropTypes.bool
  }).isRequired,
  cabinetFilterOptions: PropTypes.shape({
    currentCabinet: PropTypes.string
  }).isRequired,
  cabinetFilterOptionsActions: PropTypes.shape({
    serverIndexes: PropTypes.func,
    serverIndex: PropTypes.func
  }).isRequired,
  cabinetActions: PropTypes.shape({
    serverReplaceView: PropTypes.func,
    serverFilter: PropTypes.func,
    serverIndex: PropTypes.func
  }).isRequired,
  searchPlaceholder: PropTypes.string,
  aiSearchConfig: PropTypes.shape({
    buttonTitle: PropTypes.string,
    promptPlaceholder: PropTypes.string,
    title: PropTypes.string,
    waitingMessage: PropTypes.string,
    emptyStateMessage: PropTypes.string
  }),
  username: PropTypes.string
};
export { CabinetFilterPanel as TestCabinetFilterPanel };
const cabinetFilterOptionsLayoutRevision = (state, ownProps) => {
  var _state$cabinetFilterO;
  const cid = ownProps.cabinetIdentifier;
  if (!cid) {
    return '';
  }
  const branch = (_state$cabinetFilterO = state.cabinetFilterOptions) === null || _state$cabinetFilterO === void 0 ? void 0 : _state$cabinetFilterO[cid];
  if (!branch || typeof branch !== 'object') {
    return '';
  }
  return Object.keys(branch).sort().map(fk => {
    const slot = branch[fk];
    if (!slot || typeof slot !== 'object') {
      return `${fk}:0:-`;
    }
    const len = Array.isArray(slot.list) ? slot.list.length : 0;
    const load = slot.loading ? 'L' : '-';
    return `${fk}:${len}:${load}`;
  }).join('|');
};
export default entityComposition(CabinetFilterPanel, ['cabinetFilterOptions'], {
  currentCabinet: state => {
    const {
      cabinet = {}
    } = state;
    const currentCabinet = cabinet[cabinet.currentCabinet] || {};
    return _objectSpread(_objectSpread({}, currentCabinet), {}, {
      aiError: currentCabinet.aiError || null,
      aiRequest: currentCabinet.aiRequest || false,
      aiSuccess: currentCabinet.aiSuccess || false
    });
  },
  cabinetFilterOptionsLayoutRevision
}, ['cabinet']);